// features/notifications/NotificationsPage.tsx — Notifications feed page (auth-gated).

import { useMemo, useState } from 'react';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@/ui';
import { useNotifications, useSession } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { WhoToFollowPanel } from '@/components/panels/WhoToFollowPanel';
import { HydrationState } from '@/components/HydrationState';
import { useHydrationPending } from '@/hooks/useGlobalHydration';
import type { Notification } from '@/core/models/notification';
import { networkNotificationPath } from '@/core/models/notification';
import { NotificationTabs } from './NotificationTabs';
import { NotificationCard } from './NotificationCard';
import { NotificationFiltersPanel } from './panels/NotificationFiltersPanel';
import { ActivityOverviewPanel } from './panels/ActivityOverviewPanel';
import { filterNotifications } from './filterNotifications';
import { emptyNotificationsLabel } from './meta';
import type { TypeFilter, ScopeId } from './filterNotifications';

const PAGE_SIZE = 8;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const LoadMore = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 14px;
`;

export function NotificationsPage() {
  const { isAuthenticated, currentUser } = useSession();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const pending = useHydrationPending('notifications');

  const [activeType, setActiveType] = useState<TypeFilter>('all');
  const [activeScope, setActiveScope] = useState<ScopeId>('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(
    () => filterNotifications(notifications, activeType, activeScope),
    [notifications, activeType, activeScope],
  );

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="notifications">
        <RailPanel id="filters" label="Notification filters" required>
          <NotificationFiltersPanel
            scope={activeScope}
            onScopeChange={(s) => {
              setActiveScope(s);
              setVisibleCount(PAGE_SIZE);
            }}
          />
        </RailPanel>
        <RailPanel id="people" label="People you may know">
          <WhoToFollowPanel title="People You May Know" />
        </RailPanel>
        <RailPanel id="activity" label="Activity overview">
          <ActivityOverviewPanel notifications={notifications} />
        </RailPanel>
      </RailGroup>
    ),
    [activeScope, notifications],
  );
  useRightRail(rightRail);

  if (!isAuthenticated || !currentUser) {
    return <Navigate to={`/signin?next=${encodeURIComponent('/notifications')}`} replace />;
  }

  function activate(n: Notification) {
    void markRead(n.id);
    const networkPath = networkNotificationPath(n);
    if (networkPath) { navigate(networkPath); return; }
    // BARE on purpose (1.24.50) — same reason as NotificationBell: `n.actor` is
    // whoever reacted, never the post's author, so there is no correct handle to
    // put here. The detail page relabels on arrival.
    if (n.postId) {
      navigate(
        `/post/${encodeURIComponent(n.postId)}${n.commentId ? `?comment=${encodeURIComponent(n.commentId)}` : ''}`,
      );
    } else if (n.feedbackItemId) {
      navigate(`/feedback/${n.feedbackItemId}`);
    } else if (n.type === 'council') {
      navigate('/council');
    } else if (n.actor) {
      // General actor fallback (covers follow today, and any future
      // actor-driven type without a postId) — "post" notifications carry a
      // postId and are handled above, so this only fires when there's no post.
      // This also catches `dm` notifications (actor, no postId): a dm click
      // lands on the sender's profile, not the conversation, until a
      // conversation deep-link exists (slice 3). That's the recorded spec
      // decision — a general fallback, not a per-type special case — so don't
      // narrow this branch to exclude `dm`.
      navigate(`/profile/${n.actor.handle.replace(/^@/, '')}`);
    }
  }

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <section>
      <NetworkLinks destination="notifications" />
      <Head>
        <Title>Notifications</Title>
        <Link to="/discussions?tab=invitations">Discussion invitations</Link>
        <Button variant="secondary" onClick={() => void markAllRead()} disabled={unreadCount === 0}>
          Mark all as read
        </Button>
      </Head>

      <NotificationTabs
        active={activeType}
        onChange={(t) => {
          setActiveType(t);
          setVisibleCount(PAGE_SIZE);
        }}
      />

      <List>
        {pending ? (
          <HydrationState resource="notifications" label="your notifications" />
        ) : visible.length === 0 ? (
          <Empty>{emptyNotificationsLabel(activeType, activeScope)}</Empty>
        ) : (
          visible.map((n) => <NotificationCard key={n.id} notification={n} onActivate={activate} />)
        )}
      </List>

      {hasMore && (
        <LoadMore>
          <Button variant="secondary" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
            Load More
          </Button>
        </LoadMore>
      )}
    </section>
  );
}
